import { useContext } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ThemeContext from "../lib/theme-context";
import CustomSelect from "../components/select/index";
import CustomSingleTextField from "../components/textfields/singleRow";
import AdvancedFilterControls from "./AdvancedFilterControls";
import { patternOptions } from "../lib/patternOptions";

const MultiFilterPanel = ({
  filters,
  onFiltersChange,
  logicOperator,
  onLogicOperatorChange,
}) => {
  const theme = useContext(ThemeContext);

  // Available filter fields - grouped logically
  const filterFields = [
    // Trade Info
    { value: "pair", label: "Pair", type: "text" },
    {
      value: "direction",
      label: "Direction",
      type: "select",
      options: ["Long", "Short"],
    },
    {
      value: "outcomePC",
      label: "Outcome",
      type: "select",
      options: ["Win", "Loss", "Breakeven", "In Progress"],
    },
    {
      value: "grade",
      label: "Grade",
      type: "select",
      options: ["Low Risk", "High Risk", "Invalid"],
    },
    {
      value: "duplicatedFrom",
      label: "Multi-Account",
      type: "select",
      options: ["Yes", "No"],
    },
    {
      value: "foreCastId",
      label: "Forecast",
      type: "select",
      options: ["Yes", "No"],
    },
    // Entry Info
    {
      value: "entryType",
      label: "Entry Type",
      type: "select",
      options: ["Risk", "Reduced Risk"],
    },
    {
      value: "firstEntryOrScaleIn",
      label: "Entry No",
      type: "select",
      options: ["1st Entry", "Scale In"],
    },
    { value: "entryDateTime", label: "Entry", type: "date" },
    { value: "exitDateTime", label: "Exit", type: "date" },
    { value: "duration", label: "Duration", type: "text" },
    { value: "risk", label: "Risk", type: "number" },
    { value: "stopLoss", label: "Stop Loss", type: "number" },
    { value: "pl", label: "P/L", type: "number" },
    // Patterns
    {
      value: "alphaPattern",
      label: "Alpha Pattern",
      type: "select",
      options: patternOptions.filter(item => !item.isGroup).map(item => item.value),
    },
    {
      value: "betaPattern",
      label: "Beta Pattern",
      type: "select",
      options: patternOptions.filter(item => !item.isGroup).map(item => item.value),
    },
  
    // Other
  
    {
      value: "highlightedPair",
      label: "Watchlist",
      type: "select",
      options: ["Main", "Additional", "Potential"],
    },
  ];

  // Available operators based on field type
  const getOperators = (fieldType) => {
    switch (fieldType) {
      case "text":
        return [
          { value: "contains", label: "Contains" },
          { value: "equals", label: "Equals" },
          { value: "startsWith", label: "Starts With" },
          { value: "endsWith", label: "Ends With" },
          { value: "notContains", label: "Does Not Contain" },
        ];
      case "select":
        return [
          { value: "equals", label: "Is" },
        ];
      case "date":
        return [
          { value: "equals", label: "Equals" },
          { value: "after", label: "After" },
          { value: "before", label: "Before" },
          { value: "between", label: "Between" },
        ];
      case "number":
        return [
          { value: "equals", label: "Equals" },
          { value: "greaterThan", label: "Greater Than" },
          { value: "lessThan", label: "Less Than" },
          { value: "between", label: "Between" },
          { value: "notEquals", label: "Not Equals" },
        ];
      default:
        return [{ value: "contains", label: "Contains" }];
    }
  };

  const addFilter = () => {
    const newFilter = {
      id: Date.now().toString(),
      field: "pair",
      operator: "contains",
      value: "",
    };
    onFiltersChange([...filters, newFilter]);
  };

  const updateFilter = (id, field, value) => {
    onFiltersChange(
      filters.map((filter) =>
        filter.id === id ? { ...filter, [field]: value } : filter,
      ),
    );
  };

  const removeFilter = (id) => {
    onFiltersChange(filters.filter((filter) => filter.id !== id));
  };

  const getFieldInfo = (fieldValue) => {
    return filterFields.find((f) => f.value === fieldValue);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Logic Operator Toggle */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <span
          style={{
            fontSize: "12px",
            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          Match
        </span>
        <ToggleButtonGroup
          value={logicOperator}
          exclusive
          onChange={onLogicOperatorChange}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              fontSize: "12px",
              padding: "6px 20px",
              border: `1px solid ${theme[0] === "dark" ? "#4a5568" : "#d1d5db"}`,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              fontWeight: "400",
              backgroundColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
                borderColor: theme[0] === "dark" ? "#6b7280" : "#9ca3af",
              },
              "&.Mui-selected": {
                backgroundColor: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                color: "#ffffff",
                borderColor: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: theme[0] === "dark" ? "0 0 20px rgba(28, 131, 222, 0.7), inset 0 1px 0 rgba(255,255,255,0.2)" : "0 0 20px rgba(37, 99, 235, 0.6), inset 0 1px 0 rgba(255,255,255,0.2)",
                "&:hover": {
                  backgroundColor: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
                },
              },
            },
          }}
        >
          <ToggleButton value="and">all conditions</ToggleButton>
          <ToggleButton value="or">any condition</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Filter Controls */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {filters.map((filter, index) => {
          const fieldInfo = getFieldInfo(filter.field);
          const operators = getOperators(fieldInfo?.type || "text");

          return (
            <Box
              key={filter.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                padding: "8px 0",
                borderBottom: `1px solid ${theme[0] === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
                minWidth: "100%",
                "@media (max-width: 768px)": {
                  minWidth: "280px",
                },
              }}
            >
              {/* Filter Controls */}
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  "@media (max-width: 768px)": {
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: "12px",
                  },
                }}
              >
                {/* Field Select */}
                <Box
                  sx={{
                    minWidth: 120,
                    flex: "0 0 auto",
                    "@media (max-width: 768px)": {
                      flex: "1 1 100%",
                    },
                  }}
                >
                  <CustomSelect
                    size="small"
                    placeholder="Select Field"
                    value={filter.field}
                    onChange={(e) =>
                      updateFilter(filter.id, "field", e.target.value)
                    }
                    menuItemArray={JSON.stringify(
                      filterFields.map((field) => ({
                        value: field.value,
                        label: field.label,
                      })),
                    )}
                  />
                </Box>

                {/* Operator Select */}
                <Box
                  sx={{
                    minWidth: 120,
                    flex: "0 0 auto",
                    "@media (max-width: 768px)": {
                      flex: "1 1 100%",
                    },
                  }}
                >
                  <CustomSelect
                    size="small"
                    placeholder="Select Operator"
                    value={filter.operator}
                    onChange={(e) =>
                      updateFilter(filter.id, "operator", e.target.value)
                    }
                    menuItemArray={JSON.stringify(
                      operators.map((operator) => ({
                        value: operator.value,
                        label: operator.label,
                      })),
                    )}
                  />
                </Box>

                {/* Value Input */}
                {fieldInfo?.type === "select" ? (
                  <Box
                    sx={{
                      minWidth: 140,
                      flex: 1,
                      "@media (max-width: 768px)": {
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <CustomSelect
                      size="small"
                      placeholder="Select Value"
                      value={filter.value}
                      onChange={(e) =>
                        updateFilter(filter.id, "value", e.target.value)
                      }
                      menuItemArray={JSON.stringify(
                        fieldInfo.options?.map((option) => ({
                          value: option,
                          label: option,
                        })) || [],
                      )}
                    />
                  </Box>
                ) : fieldInfo?.type === "number" &&
                    fieldInfo?.label?.includes("P/L") ? (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <AdvancedFilterControls
                      filter={filter}
                      onUpdateFilter={updateFilter}
                      fieldInfo={fieldInfo}
                    />
                  </Box>
                ) : fieldInfo?.type === "date" ? (
                  <Box
                    sx={{
                      minWidth: 250,
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      "@media (max-width: 768px)": {
                        minWidth: "100%",
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <AdvancedFilterControls
                      filter={filter}
                      onUpdateFilter={updateFilter}
                      fieldInfo={fieldInfo}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      minWidth: 140,
                      flex: 1,
                      "@media (max-width: 768px)": {
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <CustomSingleTextField
                      size="small"
                      label={
                        fieldInfo?.type === "date"
                          ? "Date"
                          : fieldInfo?.type === "number"
                            ? "Number"
                            : "Text"
                      }
                      value={filter.value}
                      onChange={(e) =>
                        updateFilter(filter.id, "value", e.target.value)
                      }
                      type={
                        fieldInfo?.type === "date"
                          ? "date"
                          : fieldInfo?.type === "number"
                            ? "number"
                            : "text"
                      }
                      variant="outlined"
                      sx={{ width: "100%" }}
                    />
                  </Box>
                )}

                {/* Delete Filter */}
                <IconButton
                  size="small"
                  onClick={() => removeFilter(filter.id)}
                  sx={{
                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                    flexShrink: 0,
                    "&:hover": {
                      color: theme[0] === "dark" ? "#f23645" : "#dc2626",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Box>

            </Box>
          );
        })}

        {/* Add Filter Button */}
        <Box
          sx={{ display: "flex", justifyContent: "center", paddingTop: "8px" }}
        >
          <IconButton
            onClick={addFilter}
            sx={{
              backgroundColor:
                theme[0] === "dark"
                  ? "rgba(28, 131, 222, 0.1)"
                  : "rgba(37, 99, 235, 0.1)",
              color: theme[0] === "dark" ? "#1C83DE" : "#2563eb",
              border: `1px solid ${theme[0] === "dark" ? "rgba(28, 131, 222, 0.3)" : "rgba(37, 99, 235, 0.3)"}`,
              "&:hover": {
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(28, 131, 222, 0.2)"
                    : "rgba(37, 99, 235, 0.2)",
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default MultiFilterPanel;
